import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Box, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import { TreeItemLabelInput } from '@mui/x-tree-view';
import {
  TreeItemContent,
  TreeItemIconContainer,
  TreeItemGroupTransition,
  TreeItemLabel,
  TreeItemRoot,
} from '@mui/x-tree-view/TreeItem';
import { TreeItemDragAndDropOverlay } from '@mui/x-tree-view/TreeItemDragAndDropOverlay';
import { TreeItemIcon } from '@mui/x-tree-view/TreeItemIcon';
import { TreeItemProvider } from '@mui/x-tree-view/TreeItemProvider';
import { useTreeItem } from '@mui/x-tree-view/useTreeItem';
import { RichTreeViewPro } from '@mui/x-tree-view-pro/RichTreeViewPro';
import React, { useState, useEffect, useMemo, useCallback } from 'react';

import {
  getDepartments,
  addDepartment,
  moveDepartment,
  deleteDepartment,
  updateDepartment,
  changeBlockedDepartment,
} from '../../../packages/common/api';
import WarningModal from '../../../packages/common/shared-ui/src/components/WarningModal';
import ArrowDownIcon from '../../../packages/common/shared-ui/src/icons/ArrowDownIcon';
import ArrowUpIcon from '../../../packages/common/shared-ui/src/icons/ArrowUpIcon';
import BlockColoredIcon from '../../../packages/common/shared-ui/src/icons/BlockColoredIcon';
import DeleteColoredIcon from '../../../packages/common/shared-ui/src/icons/DeleteColoredIcon';
import PlusIcon from '../../../packages/common/shared-ui/src/icons/PlusIcon';
import ReorderIcon from '../../../packages/common/shared-ui/src/icons/ReorderIcon';
import { useTranslation } from '../../../packages/common/utils/getLocalString';
import CommonPage from '../CommonPage/CommonPage';

// Original class: departmentsContainer
const StyledDepartmentsContainer = styled(Box)(theme => ({
  '&.MuiBox-root': {
    backgroundColor: '#fff',
    padding: '16px',
    borderRadius: '4px',
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
  },
}));
const Departments = () => {
  const [showDepartmentWarning, setShowDepartmentWarning] = useState(false);
  const getLocalString = useTranslation();
  const pageTitle = getLocalString('Departments', 'title');
  const [departments, setDepartments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const isItemDisabled = item => !!item.disabled;
  const handleModalAction = useCallback(async e => {
    switch (e.currentTarget.attributes.value.value) {
      case 'cancel':
        setShowDepartmentWarning(false);
        break;
    }
  }, []);
  const findNodeById = useCallback((nodes, id, findParent = false) => {
    if (!findParent) {
      for (const node of nodes) {
        if (node.id === id) {
          return node;
        }
        if (node.children) {
          const found = findNodeById(node.children, id, false);
          if (found) {
            return found;
          }
        }
      }
      return null;
    }
    for (const node of nodes) {
      if (node.children) {
        for (const child of node.children) {
          if (child.id === id) {
            return node;
          }
        }
        const found = findNodeById(node.children, id, true);
        if (found) {
          return found;
        }
      }
    }
    return null;
  }, []);
  const findNodeAndRemove = useCallback((nodes, idToRemove) => {
    for (let i = 0; i < nodes.length; i++) {
      if (nodes[i].id === idToRemove) {
        return nodes.splice(i, 1)[0];
      }
      if (nodes[i].children) {
        const found = findNodeAndRemove(nodes[i].children, idToRemove);
        if (found) {
          return found;
        }
      }
    }
    return null;
  }, []);
  const handleItemMove = useCallback(
    async params => {
      try {
        const { itemId, newPosition } = params;
        const isNestingOperation =
          newPosition.parentId && newPosition.index === 0;
        const moveData = {
          parentId: newPosition.parentId ? Number(newPosition.parentId) : null,
          topId: isNestingOperation
            ? null
            : (() => {
                const parent = newPosition.parentId
                  ? findNodeById(departments, newPosition.parentId)
                  : { children: departments };
                if (parent?.children && parent.children.length > 0) {
                  if (newPosition.index === 0) {
                    return null;
                  }
                  return (
                    Number(parent.children[newPosition.index - 1]?.id) || null
                  );
                }
                return null;
              })(),
        };
        setDepartments(prev => {
          const newTree = JSON.parse(JSON.stringify(prev));
          const movedNode = findNodeAndRemove(newTree, itemId);
          if (!movedNode) {
            return prev;
          }
          if (newPosition.parentId) {
            const parent = findNodeById(newTree, newPosition.parentId);
            if (parent) {
              parent.children = parent.children || [];
              if (isNestingOperation) {
                parent.children.unshift(movedNode);
              } else {
                parent.children.splice(newPosition.index, 0, movedNode);
              }
            }
          } else {
            newTree.splice(newPosition.index, 0, movedNode);
          }
          return newTree;
        });
        const response = await moveDepartment(Number(params.itemId), moveData);
      } catch (err) {
        setError(err);
      } finally {
        setIsLoading(false);
      }
    },
    [departments, findNodeById, findNodeAndRemove],
  );
  const addNewDepartment = useCallback(async () => {
    try {
      const newDepartment = {
        name: getLocalString('Departments', 'labels', 'newDepartment'),
      };
      const response = await addDepartment(newDepartment);
      const newFromServerDepartment = {
        id: response.data.id,
        label: response.data.name,
        disabled: response.data.isBlocked,
        parentId: response.data.parentId,
        children: [],
      };
      setDepartments(prev => [...prev, newFromServerDepartment]);
    } catch (error) {
      console.error('Error adding department:', error);
    }
  }, [getLocalString]);
  const deleteDepartmentFunction = useCallback(
    async itemId => {
      try {
        const response = await deleteDepartment(itemId);

        if (response.statusText === 'OK') {
          setDepartments(prev => {
            const removeNode = nodes => {
              return nodes.filter(node => {
                if (node.id === itemId) {
                  return false;
                }
                if (node.children) {
                  node.children = removeNode(node.children);
                }
                return true;
              });
            };
            return removeNode(prev);
          });
        }
      } catch (error) {
        if (error.response.status === 420) {
          setError(
            getLocalString(
              'Departments',
              'errors',
              error.response.data.errorCode,
            ),
          );
          setShowDepartmentWarning(true);
        }
      }
    },
    [getLocalString],
  );
  const lockUnlockDepartment = useCallback(
    async itemId => {
      try {
        const response = await changeBlockedDepartment(itemId);
        if (response.data?.status === 'ok') {
          setDepartments(prev => {
            const toggleDisabled = nodes => {
              return nodes.map(node => {
                if (node.id === itemId) {
                  return { ...node, disabled: !node.disabled };
                }
                if (node.children) {
                  return { ...node, children: toggleDisabled(node.children) };
                }
                return node;
              });
            };
            return toggleDisabled(prev);
          });
        }
      } catch (error) {
        if (error.response.status === 420) {
          setError(
            getLocalString(
              'Departments',
              'errors',
              error.response.data.errorCode,
            ),
          );
          setShowDepartmentWarning(true);
        }
      }
    },
    [getLocalString],
  );
  const handleLabelChange = useCallback(async (itemId, newLabel) => {
    if (!newLabel || newLabel.trim() === '') {
      return;
    }
    try {
      const response = await updateDepartment(itemId, { name: newLabel });

      if (!response.data) {
        throw new Error('Failed to update department');
      }

      return true;
    } catch (err) {
      console.error('Error updating department:', err);
      return false;
    }
  }, []);
  const CustomTreeItem = React.forwardRef(function CustomTreeItem(props, ref) {
    const { id, itemId, label, disabled, children, ...other } = props;
    const {
      getContextProviderProps,
      getRootProps,
      getContentProps,
      getIconContainerProps,
      getCheckboxProps,
      getLabelProps,
      getLabelInputProps,
      getGroupTransitionProps,
      getDragAndDropOverlayProps,
      status,
      actions,
    } = useTreeItem({ id, itemId, children, label, disabled, rootRef: ref });
    const dragAndDropOverlayStyles = {
      '&[data-position="insert"]': {
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 16,
          right: 16,
          height: 2,
          backgroundColor: '#1976d2',
          zIndex: 1000,
        },
      },
      '&[data-position="nest"]': {
        outline: '2px solid #ff4081',
      },
    };
    const { draggable, onDragStart, onDragOver, onDragEnd, ...otherRootProps } =
      getRootProps(other);
    const [dragPosition, setDragPosition] = useState({
      x: 0,
      y: 0,
      show: false,
      text: '',
    });

    const handleDrag = useCallback(e => {
      setDragPosition({
        x: e.clientX,
        y: e.clientY,
        show: true,
        text: e.currentTarget.dataset.position === 'insert' ? 'MOVE' : 'INSERT',
      });
    }, []);

    const handleDragEnd = useCallback(() => {
      setDragPosition(prev => ({ ...prev, show: false }));
    }, []);
    return (
      <TreeItemProvider
        sx={{
          borderBottom: 0,
          '& .MuiTreeItem-content[data-disabled]': {
            backgroundColor: 'red',
            color: 'red',
          },
        }}
        {...getContextProviderProps()}
      >
        <TreeItemRoot
          sx={{
            borderBottom: 0,
          }}
          {...getRootProps(other)}
        >
          <TreeItemContent
            sx={{
              padding: 0,
              opacity: '1 !important',
              borderBottom: '1px solid #E4E4EF',
            }}
            {...getContentProps()}
          >
            <TreeItemIconContainer
              sx={{
                width: '32px',
                height: '100%',
                padding: '12px 8px',
                position: 'relative',
                borderRight: '1px solid #E4E4EF',
              }}
            >
              <ReorderIcon
                sx={{
                  width: '17px',
                }}
                viewBox='0 0 16 16'
              />
            </TreeItemIconContainer>
            <TreeItemIconContainer
              sx={{
                width: '32px',
                height: '100%',
                padding: '10px 8px',
                position: 'relative',
                borderRight: '1px solid #E4E4EF',
              }}
            >
              <DeleteColoredIcon
                onClick={() => deleteDepartmentFunction(itemId)}
                sx={{
                  width: '24px',
                  height: '24px',
                  position: 'relative',
                  top: '4px',
                  left: '2px',
                }}
              />
            </TreeItemIconContainer>
            <TreeItemIconContainer
              sx={{
                width: '32px',
                height: '100%',
                padding: '12px 8px',
                position: 'relative',
                borderRight: '1px solid #E4E4EF',
              }}
            >
              <BlockColoredIcon
                sx={{
                  width: '23px',
                  height: '16px',
                  color: '#656985',
                }}
                viewBox='0 0 16 16'
                onClick={() => lockUnlockDepartment(itemId)}
                color={status.disabled ? '#656985' : '#DCDFF4'}
              />
            </TreeItemIconContainer>
            <TreeItemIconContainer
              sx={{
                width: 'auto',
                marginLeft:
                  'calc(var(--TreeView-itemChildrenIndentation, 32px) * var(--TreeView-itemDepth, 3))',
              }}
            >
              {status.editing ? (
                <TreeItemLabelInput
                  sx={{
                    width: 'auto',
                    fontSize: '14px',
                    padding: '10px',
                    opacity: status.disabled ? 0.38 : 1,
                  }}
                  {...getLabelInputProps()}
                />
              ) : (
                <TreeItemLabel
                  sx={{
                    width: 'auto',
                    fontSize: '14px',
                    padding: '10px',
                    opacity: status.disabled ? 0.38 : 1,
                  }}
                  {...getLabelProps()}
                />
              )}
            </TreeItemIconContainer>
            <TreeItemIconContainer {...getIconContainerProps()}>
              <TreeItemIcon status={status} />
            </TreeItemIconContainer>
            <TreeItemDragAndDropOverlay
              {...getDragAndDropOverlayProps()}
              onDragOver={handleDrag}
              onDragEnd={handleDragEnd}
              sx={{
                '&[data-position="insert"]': {
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 16,
                    right: 16,
                    height: 2,
                    backgroundColor: '#1976d2',
                    zIndex: 1000,
                  },
                },
                '&[data-position="nest"]': {
                  outline: '2px solid #ff4081',
                },
              }}
            />
          </TreeItemContent>
          {children && (
            <TreeItemGroupTransition {...getGroupTransitionProps()} />
          )}
        </TreeItemRoot>
        {dragPosition.show && (
          <Box
            sx={{
              position: 'fixed',
              left: dragPosition.x + 10,
              top: dragPosition.y - 30,
              backgroundColor: 'white',
              padding: '4px 8px',
              borderRadius: 4,
              boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
              fontSize: 12,
              fontWeight: 'bold',
              color: dragPosition.text === 'MOVE' ? '#1976d2' : '#ff4081',
              zIndex: 2000,
              pointerEvents: 'none',
            }}
          >
            {dragPosition.text}
          </Box>
        )}
      </TreeItemProvider>
    );
  });
  const transformToTree = useCallback(data => {
    if (!data) {
      return [];
    }
    const itemsMap = {};
    data.forEach(item => {
      itemsMap[item.rowId] = {
        id: item.rowId,
        label: item.rowName.trim(),
        disabled: item.services.isBlocked,
        children: [],
      };
    });

    const tree = [];
    data.forEach(item => {
      const node = itemsMap[item.rowId];
      const parentId = item.services.parentId;

      if (parentId === null) {
        tree.push(node);
      } else if (itemsMap[parentId]) {
        itemsMap[parentId].children.push(node);
      }
    });
    data.forEach(item => {
      if (item.services.children?.length) {
        const node = itemsMap[item.rowId];
        const childOrderMap = {};

        item.services.children.forEach((childId, index) => {
          childOrderMap[childId] = index;
        });

        node.children.sort((a, b) => childOrderMap[a.id] - childOrderMap[b.id]);
      }
    });

    return tree;
  }, []);

  const fetchDepartments = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await getDepartments();
      const transformedData = transformToTree(response.data.rows);
      setDepartments(transformedData);
      return transformedData;
    } catch (err) {
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, [transformToTree]);

  useEffect(() => {
    const loadData = async () => {
      const data = await fetchDepartments();
    };

    loadData();
  }, [fetchDepartments]);
  return (
    <CommonPage
      pageTitle={pageTitle}
      loading={isLoading}
      component={'Departments'}
    >
      <WarningModal
        onlyCancel
        isWarningModalOpen={showDepartmentWarning}
        handleToggle={handleModalAction}
        closeWarningModal={() => setShowDepartmentWarning(false)}
        title={error}
      />
      <StyledDepartmentsContainer
        sx={{
          marginTop: '30px',
        }}
      >
        <RichTreeViewPro
          defaultExpandedItems={['']}
          items={departments}
          itemsReordering
          isItemEditable
          slots={{
            expandIcon: ArrowDownIcon,
            collapseIcon: ArrowUpIcon,
            item: CustomTreeItem,
          }}
          slotProps={{
            dragAndDropOverlay: {
              sx: {
                '&[data-position="insert"]': {
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 16,
                    right: 16,
                    height: 2,
                    backgroundColor: '#1976d2',
                    zIndex: 1000,
                  },
                },
                '&[data-position="nest"]': {
                  outline: '2px solid #ff4081',
                },
              },
            },
          }}
          isItemDisabled={isItemDisabled}
          onItemPositionChange={handleItemMove}
          onItemLabelChange={handleLabelChange}
        />
        <Box
          sx={{
            padding: '8px 16px',
          }}
        >
          <IconButton onClick={addNewDepartment} sx={{ padding: '0' }}>
            <PlusIcon width='24' height='24' viewBox='0 0 24 24' />
          </IconButton>
        </Box>
      </StyledDepartmentsContainer>
    </CommonPage>
  );
};

export default Departments;
